import { addRefreshAuthToAuthProvider, type AuthProvider, type UserIdentity as RaUserIdentity } from "react-admin";
import { apiKey, authURL, imageURL, parseJwt, refreshAuth, type AccessToken } from "./httpClient";
import type { Role, ValuesOf } from "../models/enums";

export interface UserIdentity extends RaUserIdentity {
    id: number;
    role: string;
}

export const getAuthProvider = ({ accountId }: ExtendedAuthProviderOptions): ExtendedAuthProvider => {

    return addRefreshAuthToAuthProvider({
        async login(params) {
            const { email, password } = params as LoginWithEmailPasswordParams;

            if (email && password) {
                const { access_token, refresh_token } = await fetch(`${authURL}/token?grant_type=password`, {
                    body: JSON.stringify({ email, password }),
                    method: "POST",
                    headers: { apiKey: apiKey },
                }).then(x => x.json());
                localStorage.setItem("access_token", access_token);
                localStorage.setItem("refresh_token", refresh_token);
                window.dispatchEvent(new CustomEvent("login", { detail: { token: access_token } }));
                
                
                return { access_token, refresh_token };
            }
            const { phone, token } = params as LoginWithPhoneParams;
            if (phone && token) {
                const { access_token, refresh_token } = await fetch(`${authURL}/verify`, {
                    body: JSON.stringify({ phone, token, type: "sms" }),
                    method: "POST",
                    headers: { apiKey: apiKey },
                }).then(x => x.json());
                localStorage.setItem("access_token", access_token);
                localStorage.setItem("refresh_token", refresh_token);
                window.dispatchEvent(new CustomEvent("login", { detail: { token: access_token } }));
                
                return { access_token, refresh_token };
            }

            const { provider } = params as LoginWithOAuthParams;
            if (provider) {
                const url = await getUrlForProvider(`https://api.nortech.me/auth/v1/authorize`, provider, {
                    redirectTo: `https://nortech.me?state=${encodeURIComponent(window.location.href)}`,
                });
                window.location.assign(url);
                // To avoid react-admin to consider this as an immediate success,
                // we return a rejected promise that is handled by the default OAuth login buttons
                return Promise.reject();
            }
            return Promise.reject(new Error("Invalid login parameters"));
        },
        async otp(phone: string) {
            const { message_id } = await fetch(`${authURL}/otp`, {
                body: JSON.stringify({ phone }),
                method: "POST",
                headers: { apiKey: apiKey },
            }).then(x => x.json());
            console.log(message_id);
        },
        async setPassword({ password }: SetPasswordParams): Promise<undefined> {
            console.log(password);
            return undefined;
        },
        signup(username: string, password: string) {
            console.log(username, password);
        },
        async resetPassword(params: ResetPasswordParams): Promise<undefined> {
            console.log(params);

            return undefined;
        },
        async logout(): Promise<void> {
            // /api/auth/v1/logout?scope=global
            localStorage.removeItem("access_token");
            localStorage.removeItem("refresh_token");
        },
        async checkError(error: Record<string, unknown>): Promise<void> {
            if (error.status === 401) {
                return Promise.reject();
            }

            return Promise.resolve();
        },
        async handleCallback() {},
        async checkAuth(params): Promise<void> {
            const path = window.location.pathname;
            if (["/signup", "/about", "/store"].includes(path)) {
                return Promise.resolve();
            }
            if (params?.route === "dashboard") {
                return Promise.resolve();
            }
            const token = localStorage.getItem("access_token");
            if (token) {
                return Promise.resolve();
            }

            return Promise.reject(params);
        },
        async getPermissions(): Promise<UserIdentity> {
            const token = localStorage.getItem("access_token");
            const user = token ? parseJwt(token) : null;
            return user ? identity(accountId, user) : {
                id: 0,
                fullName: "Anon",
                avatar: undefined,
                role: "None",
            } as UserIdentity;;
        },
        async getIdentity(): Promise<UserIdentity> {
            const token = localStorage.getItem("access_token");
            const user = token ? parseJwt(token) : null;
            return user ? identity(accountId, user) : {
                id: 0,
                fullName: "Anon",
                avatar: undefined,
                role: "None",
            } as UserIdentity;;
        },
    }, refreshAuth);
};

async function getUrlForProvider(
    url: string,
    provider: string,
    options: {
        redirectTo?: string;
        scopes?: string;
        queryParams?: { [key: string]: string };
        skipBrowserRedirect?: boolean;
    }
) {
    const urlParams: string[] = [`provider=${encodeURIComponent(provider)}`];
    if (options?.redirectTo) {
        urlParams.push(`redirect_to=${encodeURIComponent(options.redirectTo)}`);
    }
    if (options?.scopes) {
        urlParams.push(`scopes=${encodeURIComponent(options.scopes)}`);
    }

    const [codeChallenge, codeChallengeMethod] = await getCodeChallengeAndMethod();

    const flowParams = new URLSearchParams({
        code_challenge: `${encodeURIComponent(codeChallenge)}`,
        code_challenge_method: `${encodeURIComponent(codeChallengeMethod)}`,
    });
    urlParams.push(flowParams.toString());

    if (options?.queryParams) {
        const query = new URLSearchParams(options.queryParams);
        urlParams.push(query.toString());
    }
    if (options?.skipBrowserRedirect) {
        urlParams.push(`skip_http_redirect=${options.skipBrowserRedirect}`);
    }

    return `${url}?${urlParams.join("&")}`;
}

async function generatePKCEChallenge(verifier: string) {
    const hashed = await sha256(verifier);
    return btoa(hashed).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function sha256(randomString: string) {
    const encoder = new TextEncoder();
    const encodedData = encoder.encode(randomString);
    const hash = await crypto.subtle.digest("SHA-256", encodedData);
    const bytes = new Uint8Array(hash);

    return Array.from(bytes)
        .map(c => String.fromCharCode(c))
        .join("");
}

async function getCodeChallengeAndMethod(isPasswordRecovery = false) {
    const codeVerifier = generatePKCEVerifier();
    let storedCodeVerifier = codeVerifier;
    if (isPasswordRecovery) {
        storedCodeVerifier += "/PASSWORD_RECOVERY";
    }
    localStorage.setItem("code_verifier", storedCodeVerifier);
    const codeChallenge = await generatePKCEChallenge(codeVerifier);
    const codeChallengeMethod = codeVerifier === codeChallenge ? "plain" : "s256";
    return [codeChallenge, codeChallengeMethod];
}

function generatePKCEVerifier() {
    const verifierLength = 56;
    const array = new Uint32Array(verifierLength);
    if (typeof crypto === "undefined") {
        const charSet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
        const charSetLen = charSet.length;
        let verifier = "";
        for (let i = 0; i < verifierLength; i++) {
            verifier += charSet.charAt(Math.floor(Math.random() * charSetLen));
        }
        return verifier;
    }
    crypto.getRandomValues(array);
    return Array.from(array, dec => ("0" + dec.toString(16)).substr(-2)).join("");
}

export type GetIdentity = (user: unknown) => Promise<UserIdentity>;
export type ExtendedAuthProviderOptions = {
    redirectTo?: string;
    accountId: number;
};

type LoginWithEmailPasswordParams = {
    email: string;
    password: string;
};

type LoginWithOAuthParams = {
    provider: string;
};

type LoginWithMagicLink = {
    email: string;
};

type LoginWithPhoneParams = {
    phone: string;
    token: string;
};

export interface ExtendedAuthProvider extends AuthProvider {
    login: (params: LoginWithEmailPasswordParams | LoginWithMagicLink | LoginWithOAuthParams | LoginWithPhoneParams) => ReturnType<AuthProvider["login"]>;
    setPassword: (params: SetPasswordParams) => Promise<void>;
    resetPassword: (params: ResetPasswordParams) => Promise<void>;
}

export type SetPasswordParams = {
    access_token: string;
    refresh_token: string;
    password: string;
};

export type ResetPasswordParams = {
    email: string;
    redirectTo?: string;
    captchaToken?: string;
};

export const identity = (accountId: number, { app_metadata, user_metadata }: AccessToken) => {
    const { claims_admin, access } = app_metadata;
    let role: ValuesOf<typeof Role> = "None";
    if (claims_admin) {
        role = "Admin";
    } else if (access[0] === accountId) {
        role = "Owner";
    } else if (access.some((x: number) => x === accountId)) {
        role = "Employee";
    }
    return {
        id: access[0],
        fullName: user_metadata.full_name,
        avatar: user_metadata.picture ? imageURL(user_metadata.picture) : undefined,
        role,
    } as UserIdentity;
};
